import { Alert, Card, CardContent, Stack as MuiStack } from '@mui/material';
import { useState } from 'react';

import { useOrderActions, useOrderState } from '../../../context';
import {
  Schedule,
  Step,
  useJobDraftActions,
  useJobDraftState,
} from '../../context';
import BottomBar from '../BottomBar';
import Layout from '../Layout';

import AccountCard from './AccountCard';
import AddBillingAccount from './AddBillingAccount';
import NoBillAccount from './NoBillAccount';

import { PlusIcon } from '@/assets/icons';
import Button from '@/components/Button';
import LoadingState from '@/components/LoadingState';
import SingleColumnLayout from '@/components/SingleColumnLayout';
import Stack from '@/components/Stack';
import { Heading } from '@/components/Typography';
import Text from '@/elements/Text';
import {
  useDefaultRateByCityQuery,
  useGetCustomerAccountQuery,
} from '@/graphql';
import useMediaQuery from '@/hooks/useMediaQuery';
import { theme } from '@/styles/colors';
import { Scalars } from '@/types/graphql';
import { isWeb } from '@/util/platform';

type Props = {
  setStep: (step: Step) => Scalars['Void'];
};
const updateRatesInSch = (
  schedules: Schedule[],
  rates: { payRate: number; costRate: number }
) => {
  return schedules.map((sch) => {
    const payRate = sch.isHoliday
      ? rates.payRate * (sch.rate || 1)
      : rates.payRate;
    const costRate = sch.isHoliday
      ? rates.costRate * (sch.rate || 1)
      : rates.costRate;

    return {
      ...sch,
      minPay: rates.payRate,
      minCost: rates.costRate,
      originalPayRate: rates.payRate,
      originalCostRate: rates.costRate,
      payRate,
      costRate,
    };
  });
};

const PaymentStep = ({ setStep }: Props) => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const jobState = useJobDraftState();
  const { billing, jobs } = useOrderState();
  const { updateSchedules } = useJobDraftActions();
  const { addJob, setBilling, replaceJob } = useOrderActions();
  const isWebView = isWeb();

  const skillWithAddress = [
    ...(jobs?.map((job) => ({
      skillId: job.skill!.id,
      addressId: job.address!.id,
    })) || []),
    { skillId: jobState.skill!.id, addressId: jobState.address!.id },
  ];

  const { data: defaultRates, refetch: refetchDefaultRates } =
    useDefaultRateByCityQuery({
      variables: {
        skillId: jobState.skill!.id,
        addressId: jobState.address!.id,
      },
    });
  const { data, loading, refetch } = useGetCustomerAccountQuery({
    variables: {
      customerId: billing!.customer.id,
      skillIds: skillWithAddress.map((sa) => sa.skillId),
    },
    notifyOnNetworkStatusChange: true,
  });

  const [showAddBilling, setShowAddBilling] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(billing?.account?.id);
  const [hideAlert, setHideAlert] = useState(false);

  const accounts =
    data?.accounts?.filter((acc) => {
      if (acc.rates?.length === 0) return true;
      for (const sa of skillWithAddress) {
        const found = !!acc.rates?.find(
          (r) => r.address?.id === sa.addressId && r.skill?.id === sa.skillId
        );
        if (!found) return false;
      }
      return true;
    }) || [];
  const accountChange =
    billing?.account?.id && selectedAccount !== billing.account.id;
  const handleSuccessAddBilling = () => {
    refetch();
    setShowAddBilling(false);
  };
  const handleContinue = async () => {
    setIsUpdating(true);
    if (accountChange) {
      // change Billing account
      const newAccount = accounts.find((acc) => acc.id === selectedAccount)!;
      let rates: any = {};
      let applyCustom = false;
      if (newAccount.rates?.length) {
        // apply custom rates
        const customRates = newAccount.rates.find(
          (r) =>
            r.skill?.id === jobState.skill?.id &&
            r.address?.id === jobState.address?.id
        )!;
        rates = { payRate: customRates.pay, costRate: customRates.cost };
        applyCustom = true;
      } else {
        // apply default rates
        rates = {
          payRate: defaultRates?.defaultRatesBySkill?.payRate,
          costRate: defaultRates?.defaultRatesBySkill?.costRate,
        };
      }
      const updatedSchs = updateRatesInSch(jobState.schedules, rates);
      updateSchedules({ schedules: updatedSchs });

      await updatePrevJobs(applyCustom, newAccount);
      setBilling({ ...billing, account: newAccount as any });
      addJob({ ...jobState, schedules: updatedSchs });
    } else if (billing && !billing!.account) {
      // new customer case
      const newAccount = accounts.find((acc) => acc.id === selectedAccount)!;
      setBilling({ ...billing, account: newAccount as any });
      addJob({ ...jobState });
    } else {
      addJob({ ...jobState });
    }
    setIsUpdating(false);
    setStep(Step.SUBMISSION);
  };

  const updatePrevJobs = async (applyCustom, newAccount) => {
    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      let rates: any = {};
      if (applyCustom) {
        const customRates = newAccount.rates.find(
          (r) =>
            r.skill?.id === job.skill!.id && r.address?.id === job.address!.id
        )!;
        rates = { payRate: customRates.pay, costRate: customRates.cost };
      } else {
        const defRates = await refetchDefaultRates({
          skillId: job.skill!.id,
          addressId: job.address!.id,
        });
        if (defRates.data.defaultRatesBySkill) {
          rates = {
            payRate: defRates.data.defaultRatesBySkill.payRate!,
            costRate: defRates.data.defaultRatesBySkill.costRate!,
          };
        }
      }
      const newSch = updateRatesInSch(job.schedules, rates);
      replaceJob(i, { ...job, schedules: newSch });
    }
  };

  return (
    <Layout>
      <SingleColumnLayout
        noPadding={phoneOnly}
        size={phoneOnly ? 'full' : 'md'}
      >
        {accountChange && !hideAlert && (
          <Alert
            icon={false}
            sx={{
              backgroundColor: theme.themeBackground,
              color: 'black',
              borderRadius: '8px',
              width: '95%',
              top: '-20px',
            }}
            onClose={() => {
              setHideAlert(true);
            }}
          >
            <b> Change Billing Address:</b> Job rates could fluctuate on the
            basis of selecting different billing account.
          </Alert>
        )}
        <Stack vertical gap={showAddBilling ? 12 : 16}>
          {showAddBilling && (
            <AddBillingAccount
              customer={billing!.customer}
              handleCancel={() => setShowAddBilling(false)}
              handleSuccess={handleSuccessAddBilling}
              phoneOnly={phoneOnly}
              selectedAccount={undefined}
            />
          )}
          {!showAddBilling && (
            <>
              <Heading>Select billing account</Heading>
              {loading && <LoadingState />}
              {accounts.length > 0 && (
                <MuiStack
                  direction={'row'}
                  flexWrap={'wrap'}
                  gap={2}
                  sx={{ width: '100%' }}
                >
                  {accounts.map((account) => {
                    return (
                      <AccountCard
                        key={account.id}
                        account={account}
                        handleSelect={(id) => setSelectedAccount(id)}
                        phoneOnly={phoneOnly}
                        selectedAccount={selectedAccount}
                      />
                    );
                  })}
                  {isWebView && (
                    <Card
                      sx={{
                        border: '1px dashed #D3D3D3',
                        borderRadius: '8px',
                        width: phoneOnly ? '100%' : '46%',
                        height: phoneOnly ? undefined : '310px',
                        cursor: 'pointer',
                      }}
                      variant="outlined"
                    >
                      <CardContent
                        id="add-bill-and-payment"
                        sx={{
                          display: 'flex',
                          height: '100%',
                          paddingBottom: '16px !important',
                        }}
                        onClick={() => {
                          setShowAddBilling(true);
                        }}
                      >
                        <Stack justify={'center'}>
                          <PlusIcon
                            htmlColor={theme.themeNew}
                            sx={{ width: '14px', height: '14px' }}
                          />
                          <Text color={'iconColor'} size={'lg'}>
                            Add Billing & Payment
                          </Text>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                </MuiStack>
              )}
              {!loading && accounts.length === 0 && (
                <NoBillAccount
                  customer={billing?.customer}
                  handleAddBilling={() => {
                    setShowAddBilling(true);
                  }}
                  phoneOnly={phoneOnly}
                />
              )}
            </>
          )}

          <BottomBar>
            <Button
              a11yLabel="Go back to previous step"
              appearance="outline"
              id="back-btn"
              label="Back"
              type="button"
              onClick={() => setStep(Step.DETAILS)}
            />
            <Button
              a11yLabel="Submit form"
              disabled={showAddBilling || !selectedAccount || loading}
              id="continue-btn"
              isLoading={isUpdating}
              label="Continue"
              type="submit"
              onClick={handleContinue}
            />
          </BottomBar>
        </Stack>
      </SingleColumnLayout>
    </Layout>
  );
};

export default PaymentStep;
